/* import __COLOCATED_TEMPLATE__ from './seller-profile-action-buttons.hbs'; */
import $ from 'jquery';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import { inject as service } from '@ember/service';
import { BUTTON_TYPES } from 'sh-www/constants/lsg';
import { showModal } from 'sh-www/utils/modal-controls';
import { APPLICATION } from 'sh-www/models/application';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';

import {
  BLOCK_FREELANCER_ELEMENT_ID,
  PROFILE_SUB_SECTION_STATES,
  PROFILE_OVERLAY_SCROLL_VIEW_CLASS,
} from 'sh-www/constants/components/profile';

import ProjectModal from 'sh-www/components/modals/work-samples/project-modal';
import BlockFreelancerModal from 'sh-www/components/modals/block-freelancer-modal';
import ConfirmationModal from 'sh-www/components/modals/confirmation';
import ExistingProjectsModal from 'sh-www/components/modals/projects/existing-projects-modal';
import StripeCcModal from 'sh-www/components/modals/payments/stripe-cc-modal';
import LsgSendMessageModal from 'sh-www/components/modals/lsg/lsg-send-message';
import LsgInviteToPostingModal from 'sh-www/components/modals/lsg/lsg-invite-to-posting-modal';

class SellerProfileActionButtonsComponent extends Component {
  @service('commands.application-commands')
  applicationCommandService;

  @service('states.application')
  applicationState;

  @service('state-managers.application')
  applicationStateManager;

  @service('error-tracker')
  errorTracker;

  @service('commands.company-connection-commands')
  companyConnectionCommands;

  @service('commands.user-commands')
  userCommands;

  @service
  router;

  @service
  session;

  @tracked
  isHireButtonDisabled = false;

  @tracked
  isSavingConnectionStatus = false;

  get isViewingSelf() {
    return this.args.isViewingSelf ?? false;
  }

  get showSaveOption() {
    return this.args.showSaveOption ?? true;
  }

  get isFreelancerBlocked() {
    return this.args.user.isBlocked;
  }

  get isShowSaveButton() {
    return this.showSaveOption && !this.args.user.inCurrentUsersNetwork;
  }

  get menuDropdownOptions() {
    const { currentUser } = this.session;
    const { user } = this.args;

    const menuOptions = [];

    if (currentUser.isAdmin) {
      menuOptions.push({
        title: 'Admin - View In Search',
        onClick: () => {
          this.router.transitionTo('admin.users', {
            queryParams: {
              idSlugEmail: user.id,
            },
          });
        },
      });

      menuOptions.push({
        title: 'Manage Billing',
        onClick: () => {
          this.router.transitionTo(EMBER_ROUTE_NAMES.ADMIN__USER__BILLING, user.id);
        },
      });

      return menuOptions;
    }

    menuOptions.push({
      title: 'Message',
      onClick: async () => {
        if (currentUser.company.isNeedsCreditCard) {
          await new Promise((resolve) => {
            showModal(this, StripeCcModal, {
              seller: user,
              onSuccess: resolve,
            });
          });
        }

        showModal(this, LsgSendMessageModal, {
          userToSendMessageTo: user,
        });
      },
    });

    menuOptions.push({
      title: 'Invite to posting',
      onClick: () => this.onInviteToPostingClicked(),
      hasSeparatorAfter: true,
    });

    if (this.showSaveOption && user.inCurrentUsersNetwork) {
      menuOptions.push({
        title: 'Remove from your saved list',
        onClick: () => this.onUnSaveClicked(),
      });
    }

    menuOptions.push({
      title: 'Block',
      onClick: () => this.onBlockClicked(),
    });

    return menuOptions;
  }

  get secondaryButtonType() {
    return BUTTON_TYPES.SECONDARY_REGULAR_FULL_WIDTH;
  }

  @action
  onInviteToPostingClicked() {
    const { user } = this.args;

    showModal(this, LsgInviteToPostingModal, {
      userToInvite: user,
      onInviteSucceeded: () => {
        this.applicationStateManager.addToast(`You successfully invited ${user.firstName} to your posting.`);
      },
    });
  }

  @action
  onUnSaveClicked() {
    const { currentUser } = this.session;
    const { user } = this.args;

    showModal(this, ConfirmationModal, {
      titleText: 'Remove freelancer?',
      bodyText: `Are you sure you want to remove ${user.fullName} from your 'Saved' list?`,
      actionButtonText: 'Remove',
      cancelButtonText: 'Cancel',
      isShowCancelButton: true,
      confirmAction: () => {
        this.isSavingConnectionStatus = true;

        (async () => {
          try {
            await this.companyConnectionCommands.updateNetworkConnection(
              currentUser.company,
              user,
              null,
              false,
            );
            await this.userCommands.getUser(user.id);
          } catch {
            this.applicationStateManager.addToast('Error removing user from company network');
          } finally {
            if (!this.isDestroying) {
              this.isSavingConnectionStatus = false;
            }
          }
        })();
      },
    });
  }

  @action
  onBlockClicked() {
    const { user } = this.args;

    showModal(this, BlockFreelancerModal, {
      freelancerProfile: user,
    });
  }

  @action
  onAddProjectClicked() {
    const { handleSellerAddProjectDigest } = this.args;

    showModal(this, ProjectModal, {
      addProjectDigestCallback: (projectDigest) =>
        handleSellerAddProjectDigest.perform(projectDigest),
    });
  }

  @action
  async requestAQuote() {
    const { user, closeOverlay } = this.args;
    const { currentUser } = this.session;

    if (currentUser.company.isNeedsCreditCard) {
      await new Promise((resolve) => {
        showModal(this, StripeCcModal, {
          seller: user,
          onSuccess: resolve,
        });
      });
    }

    this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__CREATE__DIRECT, user.slug, {
      queryParams: {
        type: 'quote',
      },
    });

    closeOverlay?.();
  }

  @action
  async onSaveClicked() {
    this.isSavingConnectionStatus = true;

    const { currentUser } = this.session;
    const { user } = this.args;

    try {
      await this.companyConnectionCommands.updateNetworkConnection(
        currentUser.company,
        user,
        null,
        true,
      );
      await this.userCommands.getUser(user.id);
    } catch {
      this.applicationStateManager.addToast('Error adding user to company network');
    } finally {
      if (!this.isDestroying) {
        this.isSavingConnectionStatus = false;
      }
    }
  }

  @action
  async onViewBlockUserDetailsClick(event) {
    event.stopPropagation();
    event.preventDefault();

    const { user, updateProfileSubSection } = this.args;

    // Navigate to Marketplace profile if not there already
    if (this.router.currentURL !== (`/${user.slug}`)) {
      const transition = this.router.transitionTo(EMBER_ROUTE_NAMES.PROFILE, user.slug);
      transition.data = { blockedDetails: true };
      return;
    }

    await updateProfileSubSection(PROFILE_SUB_SECTION_STATES.SUB_SECTION_YOUR_INFO);

    scheduleOnce('afterRender', this, () => {
      const blockFreelancerEl = $(`#${BLOCK_FREELANCER_ELEMENT_ID}`);
      const headerOffset = 65;
      const scrollTop = blockFreelancerEl.offset().top - headerOffset;
      const profileOverlayScrollViewEl = $(`.${PROFILE_OVERLAY_SCROLL_VIEW_CLASS}`);
      const scrollEl = profileOverlayScrollViewEl.length
        ? profileOverlayScrollViewEl
        : $([document.documentElement, document.body]);

      scrollEl.animate({ scrollTop }, 500);
    });
  }

  @action
  async hireFreelancer() {
    const { user, closeOverlay } = this.args;
    const { currentUser } = this.session;

    if (currentUser.company.isNeedsCreditCard) {
      await new Promise((resolve) => {
        showModal(this, StripeCcModal, {
          seller: user,
          onSuccess: resolve,
        });
      });
    }

    this.isHireButtonDisabled = true;

    try {
      const dashboardApplicationList = await this.applicationCommandService.loadApplications(
        undefined,
        APPLICATION.SIMPLE_STATUS.IN_PROGRESS,
        undefined,
        false,
        undefined,
        1,
        20,
        undefined,
        '',
        user.id,
        currentUser.company.id,
      );

      const { applications } = dashboardApplicationList;

      if (applications.length) {
        showModal(this, ExistingProjectsModal, {
          freelancer: user,
          freelancerProjects: applications,
        });
      } else {
        this.router.transitionTo(EMBER_ROUTE_NAMES.PROJECT__CREATE__DIRECT, user.slug);
        closeOverlay?.();
      }
    } catch (e) {
      this.errorTracker.error(
        `onHireClicked -- could not load project for freelancer id ${user.id}`,
        this,
        e,
      );
    } finally {
      if (!this.isDestroying) {
        this.isHireButtonDisabled = false;
      }
    }
  }
}

export default SellerProfileActionButtonsComponent;
